import { useQuery } from '@apollo/client/react';
import {
  Box,
  Checkbox,
  CircularProgress,
  Grid,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import { useMemo, useState } from 'react';

import ActionHeaderCell from '../ActionHeaderCell';

import OverwatchModal from '@/components/OverwatchModal';
import { AccessRegistryDocument, type GetRolesQuery } from '@/graphql/generated/graphql';

type Role = GetRolesQuery['getRoles'][number];

export interface RoleDraft {
  name: string;
  description: string;
  grants: { action: string; entity: string }[];
}

interface RoleEditorModalProps {
  open: boolean;
  /** Existing role when editing; omit to create a new one. */
  role?: Role | null;
  saving: boolean;
  handleClose: () => void;
  onSave: (draft: RoleDraft) => void;
}

const ACTION_ORDER = [
  'view',
  'create',
  'edit',
  'manage',
  'export',
  'send_email',
  'approve',
  'publish',
  'review',
  'issue',
];

const grantKey = (action: string, entity: string) => `${action}:${entity}`;

/**
 * Role grants are always company-wide and permanent — project scoping and
 * expiry are per-user concerns, so they live on direct grants only.
 */
const RoleEditorModal = ({ open, role, saving, handleClose, onSave }: RoleEditorModalProps) => {
  const { data, loading } = useQuery(AccessRegistryDocument, { fetchPolicy: 'cache-first' });

  const [name, setName] = useState(role?.name ?? '');
  const [description, setDescription] = useState(role?.description ?? '');
  const [selected, setSelected] = useState<Set<string>>(
    () => new Set((role?.grants ?? []).map((g) => grantKey(g.action, g.entity))),
  );

  const registry = data?.accessRegistry ?? [];

  const actionColumns = useMemo(() => {
    const present = new Map<string, { key: string; name: string; description?: string | null }>();
    registry.forEach((entity) =>
      entity.actions.forEach((action) => {
        if (!present.has(action.key)) {
          present.set(action.key, {
            key: action.key,
            name: action.name,
            description: action.description,
          });
        }
      }),
    );
    return ACTION_ORDER.flatMap((key) => {
      const found = present.get(key);
      return found ? [found] : [];
    });
  }, [registry]);

  if (!open) return null;

  const toggle = (action: string, entity: string) => {
    setSelected((prev) => {
      const next = new Set(prev);
      const key = grantKey(action, entity);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  };

  const toggleRow = (entity: { key: string; actions: { key: string }[] }, checked: boolean) => {
    setSelected((prev) => {
      const next = new Set(prev);
      entity.actions.forEach((action) => {
        const key = grantKey(action.key, entity.key);
        if (checked) next.add(key);
        else next.delete(key);
      });
      return next;
    });
  };

  const setColumn = (action: string, entityKeys: string[], checked: boolean) => {
    setSelected((prev) => {
      const next = new Set(prev);
      entityKeys.forEach((entity) => {
        const key = grantKey(action, entity);
        if (checked) next.add(key);
        else next.delete(key);
      });
      return next;
    });
  };

  const handleSave = () => {
    const grants = Array.from(selected).map((key) => {
      const [action, entity] = key.split(':');
      return { action, entity };
    });
    onSave({ name: name.trim(), description: description.trim(), grants });
  };

  return (
    <OverwatchModal
      open={open}
      handleClose={handleClose}
      title={role ? `Edit role — ${role.name}` : 'Create role'}
      size="lg"
      primaryButtonText={saving ? 'Saving…' : 'Save'}
      primaryButtonAction={handleSave}
      disablePrimary={saving || name.trim().length === 0}
      secondaryButtonText="Cancel"
      secondaryButtonAction={handleClose}
    >
      <Grid container spacing={2}>
        <Grid size={{ xs: 12, md: 4 }}>
          <TextField
            label="Role name"
            value={name}
            onChange={(event) => setName(event.target.value)}
            fullWidth
            size="small"
            required
          />
        </Grid>
        <Grid size={{ xs: 12, md: 8 }}>
          <TextField
            label="Description"
            value={description}
            onChange={(event) => setDescription(event.target.value)}
            fullWidth
            size="small"
          />
        </Grid>

        <Grid size={12}>
          <Typography variant="caption" color="text.secondary">
            Everyone holding this role gets these permissions company-wide. Project-specific or temporary access is
            granted per user instead.
          </Typography>
        </Grid>

        <Grid size={12}>
          {loading ? (
            <Box sx={{ p: 2, display: 'flex', alignItems: 'center', gap: 1.5 }}>
              <CircularProgress size={18} />
              <Typography variant="body2" color="text.secondary">
                Loading modules…
              </Typography>
            </Box>
          ) : (
            <TableContainer sx={{ maxHeight: '55vh', border: '1px solid rgba(0,0,0,0.12)' }}>
              <Table stickyHeader size="small">
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ fontWeight: 800, bgcolor: '#f7f7f7', minWidth: 220 }}>Module</TableCell>
                    {actionColumns.map((action) => (
                      <ActionHeaderCell
                        key={action.key}
                        action={action}
                        entities={registry}
                        selectedKeys={selected}
                        onChange={setColumn}
                      />
                    ))}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {registry.map((entity) => {
                    const supported = new Set(entity.actions.map((a) => a.key));
                    const rowKeys = entity.actions.map((a) => grantKey(a.key, entity.key));
                    const allChecked = rowKeys.every((k) => selected.has(k));
                    const someChecked = rowKeys.some((k) => selected.has(k));

                    return (
                      <TableRow key={entity.key} hover>
                        <TableCell>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <Tooltip title="Toggle every permission on this module" arrow>
                              <Checkbox
                                size="small"
                                checked={allChecked}
                                indeterminate={!allChecked && someChecked}
                                onChange={(_, checked) => toggleRow(entity, checked)}
                              />
                            </Tooltip>
                            <Typography variant="body2" fontWeight={600}>
                              {entity.name}
                            </Typography>
                          </Box>
                        </TableCell>
                        {actionColumns.map((action) => {
                          if (!supported.has(action.key)) {
                            return (
                              <TableCell key={action.key} align="center" sx={{ color: 'text.disabled' }}>
                                —
                              </TableCell>
                            );
                          }
                          return (
                            <TableCell key={action.key} align="center">
                              <Checkbox
                                size="small"
                                checked={selected.has(grantKey(action.key, entity.key))}
                                onChange={() => toggle(action.key, entity.key)}
                              />
                            </TableCell>
                          );
                        })}
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </Grid>
      </Grid>
    </OverwatchModal>
  );
};

export default RoleEditorModal;
